import { useTeam } from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { formatCurrency } from '@vrplatform/utils';
import { Group, HoverCard, Stack } from '@mantine/core';
import { Text } from '@mantine/core';
import type { party_enum } from '@vrplatform/graphql';
import { formatPercentage } from '@vrplatform/ui-common';
import { useExpenseLineCentTotal } from '../_hooks';

type Line = {
  centTotal: number;
  party: party_enum;
  taxRateId: string | null;
  markup?: {
    centTotal: number;
    taxRateId: string | null;
  } | null;
};

export const ExpenseLineAmountHoverCard = ({
  line,
  disabled,
}: {
  line: Line;
  disabled?: boolean;
}) => {
  const [{ currency }] = useTeam();
  const { data, loading, error } = useExpenseLineCentTotal({
    ...line,
    markup: line.markup?.centTotal || 0,
    taxId: line.taxRateId,
    markupTaxId: line.markup?.taxRateId || null,
    party: line.party || 'owners',
  });

  if (loading) return <LoadingIndicator size="xs" />;

  return (
    <HoverCard
      position="bottom-end"
      withArrow
      withinPortal
      width={250}
      shadow="md"
      radius="md"
      arrowOffset={10}
      disabled={disabled || loading || !!error}
    >
      <HoverCard.Target>
        <Text>
          {error?.message ??
            formatCurrency((data?.centTotal ?? 0) / 100, currency)}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap="xs">
          <Group wrap="nowrap" align="flex-start">
            <Text c="gray" flex={1}>
              Amount
            </Text>
            <Text ta="right" fw={500}>
              {formatCurrency(line.centTotal / 100, currency)}
            </Text>
          </Group>

          {!!line.taxRateId && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Amount tax{' '}
                <Text span size="xs" c="gray">
                  (
                  {formatPercentage(
                    data?.appliedRate?.percentageSuperBasePoints ?? 0,
                    'super-base-points'
                  )}
                  )
                </Text>
              </Text>
              {loading ? (
                <LoadingIndicator size="xs" />
              ) : (
                <Text ta="right" fw={500}>
                  {formatCurrency(
                    (line.centTotal *
                      ((data?.appliedRate?.percentageSuperBasePoints ?? 0) /
                        100000)) /
                      100,
                    currency
                  )}
                </Text>
              )}
            </Group>
          )}

          <Group wrap="nowrap" align="flex-start">
            <Text c="gray" flex={1}>
              Markup
            </Text>
            <Text ta="right" fw={500}>
              {formatCurrency((line.markup?.centTotal || 0) / 100, currency)}
            </Text>
          </Group>

          {!!line.markup?.taxRateId && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Markup Tax{' '}
                <Text span size="xs" c="gray">
                  (
                  {formatPercentage(
                    data?.markupRate?.percentageSuperBasePoints ?? 0,
                    'super-base-points'
                  )}
                  )
                </Text>
              </Text>
              {loading ? (
                <LoadingIndicator size="xs" />
              ) : (
                <Text ta="right" fw={500}>
                  {formatCurrency(
                    ((line.markup?.centTotal || 0) *
                      ((data?.markupRate?.percentageSuperBasePoints ?? 0) /
                        100000)) /
                      100,
                    currency
                  )}
                </Text>
              )}
            </Group>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
